// Company-level audit view of AI journal drafts (spec §10): every draft for
// the selected company, filterable by date range, approver and status. A row
// opens the full review screen (read-only once the draft is terminal) with
// its event timeline.
import { DatePicker, Modal, Select, Table } from 'antd';
import type { Dayjs } from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getApi } from 'redux/apis';
import { listJournalDrafts } from 'redux/action/journalDraftAction';
import { toastText } from 'utils/utils';
import JournalDraftReview, { StatusPill } from '../JournalDraftReview';
import type { DraftSummary, JournalDraftStatus } from '../types';
import {
	ALL_STATUSES,
	STATUS_META,
	formatMoney,
	formatTimestamp,
	formatTxnDate,
	isTerminalStatus,
	qboJournalUrl,
} from '../utils';
import styles from './index.module.scss';

const PAGE_SIZE = 20;

interface Option {
	value: string;
	label: string;
}

const JournalDraftAudit: FC = () => {
	const selectedCompanyDetails = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails
	);
	const companyId: string =
		selectedCompanyDetails?.companyId || localStorage.getItem('companyId') || '';

	const [items, setItems] = useState<DraftSummary[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [status, setStatus] = useState<JournalDraftStatus | undefined>();
	const [approvedBy, setApprovedBy] = useState<string | undefined>();
	const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
	const [companyUsers, setCompanyUsers] = useState<Option[]>([]);
	const [openDraftId, setOpenDraftId] = useState<string | null>(null);
	const [openStatus, setOpenStatus] = useState<JournalDraftStatus | null>(null);

	const fetchDrafts = useCallback(async () => {
		if (!companyId) {
			return;
		}
		setLoading(true);
		try {
			const result = await listJournalDrafts(
				{
					status,
					approvedBy,
					from: range?.[0]?.format('YYYY-MM-DD'),
					to: range?.[1]?.format('YYYY-MM-DD'),
					page,
					limit: PAGE_SIZE,
				},
				companyId
			);
			setItems(result.items);
			setTotal(result.total);
		} catch (err: any) {
			toastText(err?.response?.data?.message || "Couldn't load AI journal drafts.", 'error');
		} finally {
			setLoading(false);
		}
	}, [companyId, status, approvedBy, range, page]);

	useEffect(() => {
		fetchDrafts();
	}, [fetchDrafts]);

	// Approver options: the company's users when the viewer may list them;
	// otherwise fall back to approvers seen in the loaded drafts.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const response = await getApi('/users', { company: companyId, limit: 200, page: 1 });
				const users = (response?.data?.data ?? []) as any[];
				if (!cancelled) {
					setCompanyUsers(
						users
							.map((u) => ({
								value: u?.user?.id ?? u?.id,
								label:
									`${u?.user?.firstName ?? ''} ${u?.user?.lastName ?? ''}`.trim() ||
									u?.user?.email ||
									'Unknown user',
							}))
							.filter((o) => o.value)
					);
				}
			} catch {
				if (!cancelled) {
					setCompanyUsers([]);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [companyId]);

	const approverOptions = useMemo(() => {
		const map = new Map<string, string>();
		companyUsers.forEach((o) => map.set(o.value, o.label));
		items.forEach((d) => {
			if (d.approvedBy && !map.has(d.approvedBy.id)) {
				map.set(d.approvedBy.id, d.approvedBy.name);
			}
		});
		return Array.from(map, ([value, label]) => ({ value, label }));
	}, [companyUsers, items]);

	const columns = [
		{
			title: 'Created',
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: (v: string) => formatTimestamp(v),
		},
		{
			title: 'Journal date',
			dataIndex: 'txnDate',
			key: 'txnDate',
			render: (v: string) => formatTxnDate(v),
		},
		{
			title: 'Pay period',
			dataIndex: 'payPeriodLabel',
			key: 'payPeriodLabel',
			render: (v: string | null) => v || '—',
		},
		{
			title: 'Memo',
			dataIndex: 'memo',
			key: 'memo',
			ellipsis: true,
		},
		{
			title: 'Debits',
			dataIndex: 'totalDebit',
			key: 'totalDebit',
			align: 'right' as const,
			render: (v: string) => formatMoney(v),
		},
		{
			title: 'Credits',
			dataIndex: 'totalCredit',
			key: 'totalCredit',
			align: 'right' as const,
			render: (v: string) => formatMoney(v),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (v: JournalDraftStatus, row: DraftSummary) => (
				<span className={styles.status}>
					<StatusPill status={v} />
					<span className={styles.muted}>v{row.version}</span>
				</span>
			),
		},
		{
			title: 'Requested by',
			key: 'requestedBy',
			render: (_: unknown, row: DraftSummary) => row.requestedBy?.name || '—',
		},
		{
			title: 'Approved by',
			key: 'approvedBy',
			render: (_: unknown, row: DraftSummary) =>
				row.approvedBy ? (
					<>
						{row.approvedBy.name}
						{row.approvedAt && (
							<div className={styles.muted}>{formatTimestamp(row.approvedAt)}</div>
						)}
					</>
				) : (
					'—'
				),
		},
		{
			title: 'QuickBooks',
			key: 'qbo',
			render: (_: unknown, row: DraftSummary) => {
				const url = qboJournalUrl(row.qboTxnId);
				if (!row.qboTxnId) {
					return '—';
				}
				return (
					<>
						<a
							href={url ?? undefined}
							target="_blank"
							rel="noreferrer"
							onClick={(e) => e.stopPropagation()}
						>
							{row.qboDocNumber ? `#${row.qboDocNumber}` : `ID ${row.qboTxnId}`}
						</a>
						{row.postedAt && <div className={styles.muted}>{formatTimestamp(row.postedAt)}</div>}
					</>
				);
			},
		},
	];

	const resetPage = () => setPage(1);

	return (
		<div className={styles.audit}>
			<div className={styles.filters}>
				<DatePicker.RangePicker
					value={range as any}
					format="MM/DD/YYYY"
					placeholder={['From (journal date)', 'To']}
					onChange={(value) => {
						setRange(value as [Dayjs | null, Dayjs | null] | null);
						resetPage();
					}}
				/>
				<Select
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="Approver"
					style={{ minWidth: 180 }}
					value={approvedBy}
					options={approverOptions}
					onChange={(value) => {
						setApprovedBy(value);
						resetPage();
					}}
				/>
				<Select
					allowClear
					placeholder="Status"
					style={{ minWidth: 200 }}
					value={status}
					options={ALL_STATUSES.map((s) => ({ value: s, label: STATUS_META[s].label }))}
					onChange={(value) => {
						setStatus(value);
						resetPage();
					}}
				/>
			</div>
			<Table
				size="middle"
				rowKey="id"
				dataSource={items}
				columns={columns}
				loading={loading}
				scroll={{ x: 1100 }}
				rowClassName={() => styles.row}
				onRow={(row: DraftSummary) => ({
					onClick: () => {
						setOpenDraftId(row.id);
						setOpenStatus(row.status);
					},
				})}
				pagination={{
					current: page,
					pageSize: PAGE_SIZE,
					total,
					showSizeChanger: false,
					onChange: (p) => setPage(p),
				}}
				locale={{ emptyText: 'No AI journal drafts match these filters.' }}
			/>
			<Modal
				open={!!openDraftId}
				width={1000}
				footer={null}
				title="AI journal draft"
				destroyOnClose
				onCancel={() => {
					setOpenDraftId(null);
					setOpenStatus(null);
					fetchDrafts();
				}}
			>
				{openDraftId && (
					<JournalDraftReview
						draftId={openDraftId}
						companyId={companyId}
						variant="full"
						readOnly={openStatus ? isTerminalStatus(openStatus) : false}
						onDraftChange={(d) => setOpenStatus(d.status)}
					/>
				)}
			</Modal>
		</div>
	);
};

export default JournalDraftAudit;
